
import { useEffect, useRef } from 'react';
import { supabase } from '../services/supabaseClient';

/**
 * A hook that triggers a data refresh when the browser tab becomes visible or focused.
 * It also ensures the Supabase Auth Session is valid before triggering the refresh.
 * 
 * @param callback The function to call when the page becomes visible (e.g., loadData)
 */
export const useAutoRefresh = (callback: () => void | Promise<void>) => {
  const savedCallback = useRef(callback);

  // Keep the callback ref up to date
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    let lastRefreshTime = Date.now();

    const handleVisibilityChange = async () => {
      // Only proceed if the tab is visible
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        // Prevent double-firing if focus and visibility events overlap closely (e.g. < 500ms)
        if (now - lastRefreshTime < 500) return;
        lastRefreshTime = now;

        // 1. Basic Network Check
        if (!navigator.onLine) {
            console.log('>>> [AutoRefresh] Offline. Skipping refresh.');
            return;
        }

        // 2. Small delay to allow network stack to stabilize on mobile wake
        await new Promise(r => setTimeout(r, 500));

        console.log('>>> [AutoRefresh] Tab active. Validating session...');

        try {
          // 3. Check Token Validity & Silent Refresh
          const { data, error } = await supabase.auth.getSession();

          if (error || !data.session) {
            console.warn('>>> [AutoRefresh] Session expired or invalid. Skipping data fetch.');
            return;
          }

          // 4. Trigger Data Refresh
          console.log('>>> [AutoRefresh] Session valid. Refreshing data view.');
          if (savedCallback.current) {
            await savedCallback.current();
          }
        } catch (e) {
          console.error('>>> [AutoRefresh] Error during revalidation:', e);
        }
      }
    };

    // Listen for visibility change (Tab switching, Minimizing)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for window focus (Clicking back into the window from another app)
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);
};
